import React, { useCallback, useEffect, useState } from "react";
import { Box, Paper, Typography, Divider, Chip } from "@mui/material";
import { authHeader } from "../../auth";

const POLL_MS = 2000;

/**
 * TelemarkRanking — classements de la page Chrono pour une course
 * TÉLÉMARK (secteur TM, choisi à la création de la course). Ajouté le
 * 01.10.2026. Remplace, pour ces courses uniquement, les deux panneaux
 * "Classement" et "Classement combiné" de TimingChronoPage :
 *
 *   Temps ski | Pénalités (style + jump) | Temps manche
 *
 * Les données viennent de /api/timing/results-telemark (pénalités par
 * manche, saisies par les juges — voir penaltiesRouter.js), déjà triées
 * par temps PÉNALITÉS COMPRISES : c'est le classement réel, le même que
 * celui envoyé au Live FIS (fisCumulativeLive.js).
 */

function formatElapsed(ms) {
    if (ms == null) return "—";
    if (ms < 0) return "⚠";
    // Tronqué au 1/100 (règle FIS), jamais arrondi.
    const totalCentis = Math.floor(ms / 10);
    const minutes = Math.floor(totalCentis / 6000);
    const seconds = Math.floor((totalCentis % 6000) / 100);
    const centis = totalCentis % 100;
    return `${minutes}:${String(seconds).padStart(2, "0")}.${String(centis).padStart(2, "0")}`;
}

const fmtPen = (s) => `+${(Number(s) || 0).toFixed(2)}`;

// Colonnes à largeur fixe pour aligner les temps d'une ligne à l'autre.
const COL = { width: 92, textAlign: "right", fontFamily: "monospace" };

function HeaderRow() {
    return (
        <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1.5, px: 1, pb: 0.5 }}>
            <Typography variant="caption" color="text.secondary" sx={COL}>
                Temps ski
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={COL}>
                Pénalités
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ ...COL, fontWeight: "bold" }}>
                Temps manche
            </Typography>
        </Box>
    );
}

export default function TelemarkRanking({ eventKey, viewRun, runCount, combineMethod, renderStatusSelect }) {
    const [runData, setRunData] = useState(null);
    const [combined, setCombined] = useState(null);
    const [error, setError] = useState("");

    const fetchJson = useCallback(async (url) => {
        const res = await fetch(url, { headers: { ...authHeader() } });
        if (!res.ok) {
            const data = await res.json().catch(() => ({}));
            throw new Error(data.error || `HTTP ${res.status}`);
        }
        return res.json();
    }, []);

    useEffect(() => {
        let cancelled = false;
        const key = encodeURIComponent(eventKey);
        const load = async () => {
            try {
                const run = await fetchJson(`/api/timing/results-telemark/${key}?view=run${viewRun ? `&run=${viewRun}` : ""}`);
                if (cancelled) return;
                setRunData(run);
                if ((runCount || 1) > 1) {
                    const comb = await fetchJson(`/api/timing/results-telemark/${key}`);
                    if (!cancelled) setCombined(comb);
                } else {
                    setCombined(null);
                }
                setError("");
            } catch (err) {
                if (!cancelled) setError(err.message);
            }
        };
        load();
        const t = setInterval(load, POLL_MS);
        return () => {
            cancelled = true;
            clearInterval(t);
        };
    }, [eventKey, viewRun, runCount, fetchJson]);

    const ranking = runData?.ranking || [];
    const runNumber = runData?.runNumber || viewRun || 1;
    const combinedRanking = combined?.ranking || [];

    return (
        <>
            <Paper variant="outlined" sx={{ p: 2 }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
                    <Typography variant="h6">
                        🏆 Classement{(runCount || 1) > 1 ? ` — Manche ${runNumber}` : ""}
                    </Typography>
                    <Chip label="Télémark — pénalités incluses" size="small" color="secondary" variant="outlined" />
                </Box>
                {error && (
                    <Typography color="error" variant="body2" sx={{ mb: 1 }}>
                        {error}
                    </Typography>
                )}
                {ranking.length === 0 ? (
                    <Typography color="text.secondary" variant="body2">
                        Aucun temps calculé pour l'instant.
                    </Typography>
                ) : (
                    <>
                        <HeaderRow />
                        <Box sx={{ maxHeight: 600, overflowY: "auto" }}>
                            {ranking.map((r, i) => {
                                const pen = (r.stylePenalty || 0) + (r.jumpPenalty || 0);
                                return (
                                    <Box key={`${r.bib}-${i}`}>
                                        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", py: 0.75, px: 1 }}>
                                            <Box>
                                                <Typography sx={{ fontWeight: "bold" }}>
                                                    {r.rank}. [{r.bib}] {r.name || "—"}
                                                </Typography>
                                                {r.nation && (
                                                    <Typography variant="caption" color="text.secondary">
                                                        {r.nation}
                                                    </Typography>
                                                )}
                                            </Box>
                                            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                                                {renderStatusSelect && renderStatusSelect(r.bib)}
                                                <Typography sx={COL}>{formatElapsed(r.skiTimeMs)}</Typography>
                                                <Box sx={COL}>
                                                    <Typography
                                                        sx={{ fontFamily: "monospace", color: pen > 0 ? "error.main" : "text.secondary" }}
                                                    >
                                                        {fmtPen(pen)}
                                                    </Typography>
                                                    {pen > 0 && (
                                                        <Typography variant="caption" color="text.secondary" sx={{ display: "block" }}>
                                                            S {(r.stylePenalty || 0).toFixed(2)} · J {(r.jumpPenalty || 0).toFixed(2)}
                                                        </Typography>
                                                    )}
                                                </Box>
                                                <Typography sx={{ ...COL, fontWeight: "bold" }}>{formatElapsed(r.totalTimeMs)}</Typography>
                                            </Box>
                                        </Box>
                                        {i < ranking.length - 1 && <Divider />}
                                    </Box>
                                );
                            })}
                        </Box>
                    </>
                )}

                {runData?.notClassified?.length > 0 && (
                    <Box sx={{ mt: 2, pt: 2, borderTop: "1px solid #ddd" }}>
                        <Typography variant="subtitle2" sx={{ fontWeight: "bold", mb: 1 }}>
                            Non classés
                        </Typography>
                        {runData.notClassified.map((n, i) => (
                            <Box key={`${n.bib}-${i}`} sx={{ display: "flex", justifyContent: "space-between", py: 0.5, px: 1 }}>
                                <Typography>
                                    [{n.bib}] {n.name || "—"}
                                    {(n.status === "DSQ" || n.status === "DSQP") && n.gateNumber ? ` — porte ${n.gateNumber}` : ""}
                                </Typography>
                                <Chip label={n.status} size="small" color="error" variant="outlined" />
                            </Box>
                        ))}
                    </Box>
                )}
            </Paper>

            {(runCount || 1) > 1 && (
                <Paper variant="outlined" sx={{ p: 2, mt: 2 }}>
                    <Typography variant="h6" mb={1}>
                        🏆 Classement combiné — {combineMethod === "best" ? "meilleure manche" : "addition des manches"},
                        pénalités incluses
                    </Typography>
                    {combinedRanking.length === 0 ? (
                        <Typography color="text.secondary" variant="body2">
                            Personne classé sur l'ensemble des manches pour l'instant.
                        </Typography>
                    ) : (
                        <Box sx={{ maxHeight: 600, overflowY: "auto" }}>
                            {combinedRanking.map((r, i) => (
                                <Box key={`${r.bib}-${i}`}>
                                    <Box sx={{ display: "flex", justifyContent: "space-between", py: 0.75, px: 1 }}>
                                        <Box>
                                            <Typography sx={{ fontWeight: "bold" }}>
                                                {r.rank}. [{r.bib}] {r.name || "—"}
                                            </Typography>
                                            {r.nation && (
                                                <Typography variant="caption" color="text.secondary">
                                                    {r.nation}
                                                </Typography>
                                            )}
                                            {/* Détail par manche : ski + pénalités = temps manche */}
                                            <Typography variant="caption" color="text.secondary" sx={{ display: "block" }}>
                                                {Object.entries(r.runs || {})
                                                    .sort(([a], [b]) => Number(a) - Number(b))
                                                    .map(([rn, entry]) => {
                                                        const t = r.runTotals?.[rn];
                                                        if (t) return `M${rn}: ${formatElapsed(t.skiMs)} ${fmtPen(t.penalty)} = ${formatElapsed(t.totalMs)}`;
                                                        return `M${rn}: ${entry?.status || "—"}`;
                                                    })
                                                    .join("  ·  ")}
                                            </Typography>
                                        </Box>
                                        <Box sx={{ textAlign: "right" }}>
                                            <Typography sx={{ fontFamily: "monospace", fontWeight: "bold" }}>
                                                {formatElapsed(r.totalTimeMs)}
                                            </Typography>
                                            {(r.stylePenalty || 0) + (r.jumpPenalty || 0) > 0 && (
                                                <Typography variant="caption" color="error.main">
                                                    dont pénalités {fmtPen((r.stylePenalty || 0) + (r.jumpPenalty || 0))}
                                                </Typography>
                                            )}
                                        </Box>
                                    </Box>
                                    {i < combinedRanking.length - 1 && <Divider />}
                                </Box>
                            ))}
                        </Box>
                    )}
                </Paper>
            )}
        </>
    );
}